import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@material-ui/core'
import { getTabsModeEnabled, getTipsEnabled } from '../../selectors/menus'
import { cartHasPriceOverrideItem, getTotal, hasCartItems2, hasOpenPriceItem } from '../../selectors/cart'
import styles from './VNCartButtons.module.scss'
import cn from 'classnames'
import useSpinner from '../../context/hooks/useSpinner'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { setRoute } from '../../VNRoutes/ActionCreators'
import { getIsPaired } from '../../VNCustomerFacingDisplay/Selectors'
import {
  setBlockRichCheckoutOrderDialog,
  setCFDNotPairedDialog,
  setConfirmPaymentDialog,
  SetOfflineDialog,
} from '../../VNDialogs/ActionCreators'
import { getOrderInProgress } from '../../selectors/order'
import { isTabbed } from '../../utils/orderStates'
import { updateHighlightedItems, updateUseQuickSearchFlag } from '../../actions/search'
import {
  getCashEnabled,
  getGiftCardEnabled,
  getHasNoOpTenders,
  getMobilePaySetting,
  getTicketEnabled,
} from '../../selectors/revenueCenter'
import { APPLICATION_STRINGS } from '../../strings'
import { getIsOrderTaxExempt } from '../../VNTaxes/Selectors'
import useOfflineModal from '../../hooks/useOfflineModal'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { setCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/ActionCreators'
import { CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import { getTender } from '../../VNPPI/Utils'
import { getPpiConfigurationError, getPpiConfigurations } from '../../VNPPI/Selectors'
import { VNPPI_TENDERS } from '../../VNPPI/Enums'
import { isFeatureOn } from '../../VNConfigurations/Selectors'
import { VNCONFIGS_FEATURE_ENABLEMENT_KEYS } from '../../VNConfigurations/Enums'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { useCartProvider } from '../../VNItems/hooks/VNCartContext'
import { SIGNATURE_THRESHOLD_AMOUNT } from '../../constants'
import { getTipOnCardReader } from '../../selectors/peripheral'
import { getBackbarMode, getNetworkAvailable } from '../../selectors/config'
import { useCharity } from '../../VNOrders/hooks/useCharity'

const UNLOCK_CART_TIME = 1500
const LOAD_REMOTE_ORDER_TOTAL_TIMEOUT = 500

const VNPaymentButtons = ({
  loadRemoteOrderTotal,
  setIsRichCheckout,
  setGetCartTotals,
  remoteOrderTotal,
  remoteOrderTotalCompleted,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const cfdMode = useSelector(getDeviceMode)
  const isCFDPaired = useSelector(getIsPaired)
  const orderInProgress = useSelector(getOrderInProgress)
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const hasCartItems = useSelector(hasCartItems2)
  const cartHasPriceOverride = useSelector(cartHasPriceOverrideItem)
  const cartHasOpenItem = useSelector(hasOpenPriceItem)
  const isOrderTaxExempt = useSelector(getIsOrderTaxExempt)
  const cashEnabled = useSelector(getCashEnabled)
  const mobilePaySetting = useSelector(getMobilePaySetting)
  const ticketEnabled = useSelector(getTicketEnabled)
  const giftCardEnabled = useSelector(getGiftCardEnabled)
  const hasNoOpTender = useSelector(getHasNoOpTenders)
  const ppiConfigurations = useSelector(getPpiConfigurations)
  const tipEnabled = useSelector(getTipsEnabled)
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  const offlineTotal = useSelector(getTotal)
  const isBackBar = useSelector(getBackbarMode)
  const { getRoundUpForCharityFeature } = useCharity()
  const { setIsCartLocked } = useCartProvider()
  const unlockTimeoutRef = useRef(null)

  const isMobileViewPort = useGetIsMobileScreen()
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const { startSpinner, stopSpinner } = useSpinner()
  const disablePPISplitOffline = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.DISABLE_PPI_SPLIT_OFFLINE),
  )

  const ppiTicketTender = getTender(ppiConfigurations, VNPPI_TENDERS.TICKET_TENDER)
  const ppiQRPayTender = getTender(ppiConfigurations, VNPPI_TENDERS.QRPAY_TENDER)
  const ppiGiftcardTender = getTender(ppiConfigurations, VNPPI_TENDERS.GIFTCARD_TENDER)
  const ppiConfigurationError = useSelector(getPpiConfigurationError)
  const networkAvailable = useSelector(getNetworkAvailable)

  // logic to determine whether to navigate to signature screen or not after selecting CC as the tender type
  const totalInCents = useMemo(() => {
    if (!networkAvailable) {
      return offlineTotal
    }

    return remoteOrderTotal?.totalAmountInCents ?? offlineTotal
  }, [remoteOrderTotal, networkAvailable, offlineTotal])

  const threshHoldExceeded =
    SIGNATURE_THRESHOLD_AMOUNT === 0 || (SIGNATURE_THRESHOLD_AMOUNT && totalInCents > SIGNATURE_THRESHOLD_AMOUNT)
  const signatureRequired = tipEnabled || threshHoldExceeded
  const cardTipEnabledOnPos = !useSelector(getTipOnCardReader) && tipEnabled

  const isUniqueInvoiceNumbersEnabled = useSelector((state) =>
    isFeatureOn(state, VNCONFIGS_FEATURE_ENABLEMENT_KEYS.UNIQUE_INVOICE_NUMBERS),
  )

  const offlineModalScanScreenMessage = isMobileViewPort
    ? 'This payment tender is not supported while the device is offline'
    : 'This payment tender is not supported\nwhile the device is offline'

  const offlineGiftCardModalMessage = isMobileViewPort
    ? 'Gift card tender is not supported while the device is offline'
    : 'Gift card tender is not supported while\n the device is offline'

  const offlineSubtext = isMobileViewPort
    ? 'Sorry, this device is now offline. Please choose another method of payment.'
    : 'Sorry, this device is now offline. Please\nchoose another method of payment.'

  // When not in CFD mode, user will be navigated either to signature or card reader screen depending on the fetched order total
  useEffect(() => {
    if (remoteOrderTotalCompleted && cfdMode !== MODES.POS) {
      if (signatureRequired && cardTipEnabledOnPos) {
        history.push('/signature')
        return
      }

      if (!isBackBar && getRoundUpForCharityFeature(totalInCents)) {
        history.push({
          pathname: '/charity',
          state: {
            fromCardLoader: true,
            totalInCents: totalInCents,
          },
        })
        return
      }

      history.push('/card-reader')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteOrderTotalCompleted])

  /**
   * Starts the CFD Payment flow
   * sets the correct states for CFD to use card payment or richcheckout
   */
  const startCFDPayment = useCallback(
    (payWithRichCheckout, isCCPayment) => {
      if (isCCPayment && !isCFDPaired) {
        stopSpinner()
        dispatch(setCFDNotPairedDialog(true))
        return
      }

      if (!isTabbed(orderInProgress)) {
        loadRemoteOrderTotal(true)
      }
      setIsRichCheckout(payWithRichCheckout)
      setGetCartTotals(true)
      dispatch(updateUseQuickSearchFlag(false))
      dispatch(updateHighlightedItems({ query: '', highlightedIds: [], focusedItemId: undefined }))
    },
    [isCFDPaired, orderInProgress, setIsRichCheckout, setGetCartTotals, dispatch, stopSpinner, loadRemoteOrderTotal],
  )

  const handleScanScreenSelection = useCallback(
    (scanScreenPath) => {
      if (tipEnabled) {
        dispatch(setRoute('/signature', { addTipThenRedirectPath: scanScreenPath })) // need to check this is the correct schema
        return
      }

      if (!isBackBar && getRoundUpForCharityFeature(totalInCents)) {
        history.push({
          pathname: '/charity',
          state: {
            fromScanScreen: true,
            totalInCents: totalInCents,
            redirectPath: scanScreenPath,
          },
        })
        return
      }

      dispatch(setRoute(scanScreenPath))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, tipEnabled],
  )

  // Payment Button Click Handlers
  const onCardPaymentHandler = useCallback(() => {
    setIsCartLocked(true)
    startSpinner()

    unlockTimeoutRef.current = setTimeout(() => {
      setIsCartLocked(false)
    }, UNLOCK_CART_TIME)

    if (cfdMode !== MODES.POS) {
      // set timeout here to ensure that the remote order total is fetched after any cart updates have been made
      setTimeout(() => {
        // use set route dispatch so it's sequentially after any cart dispatches
        loadRemoteOrderTotal(true)
      }, LOAD_REMOTE_ORDER_TOTAL_TIMEOUT)
      return
    }

    startCFDPayment(false, true)
  }, [startSpinner, cfdMode, startCFDPayment, setIsCartLocked, loadRemoteOrderTotal])

  const onCashPaymentHandler = useCallback(() => {
    dispatch(setRoute('/change-calculator'))
  }, [dispatch])

  const mobilePayHandler = useCallback(() => {
    // Rich Checkout "POS Shows QR" - Open Price and Price Override
    if (mobilePaySetting === 'pos_shows_qr' && cartHasPriceOverride) {
      dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.PRICE_OVERRIDE_RICH_CHECKOUT_CLOSING_ORDER))
      return
    }
    if (mobilePaySetting === 'pos_shows_qr' && cartHasOpenItem) {
      dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.OPEN_PRICE_RICH_CHECKOUT_CLOSING_TABS_DIALOG))
      return
    }
    if (mobilePaySetting === 'pos_shows_qr' && isOrderTaxExempt) {
      dispatch(setBlockRichCheckoutOrderDialog(true, APPLICATION_STRINGS.TAX_EXEMPT_RICH_CHECKOUT_CLOSING_TABS_DIALOG))
      return
    }

    // CFD - Rich Checkout Button
    if (cfdMode === MODES.POS && mobilePaySetting === 'pos_shows_qr') {
      startSpinner()
      startCFDPayment(true)
      return
    }
    if (mobilePaySetting === 'pos_shows_qr') {
      startSpinner()
      dispatch(setRoute('/rich-checkout'))
      return
    }

    if (ppiConfigurationError) {
      dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
      return
    }

    // qr pay for cfd
    if (cfdMode === MODES.POS) {
      checkIfOfflineWithCallback(
        () => {
          startSpinner()
          dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.QR_PAY, true))
          startCFDPayment(false)
        },
        offlineModalScanScreenMessage,
        disablePPISplitOffline,
      )
      return
    }
    checkIfOfflineWithCallback(
      () => {
        startSpinner()
        handleScanScreenSelection('/qr-pay')
      },
      offlineModalScanScreenMessage,
      disablePPISplitOffline,
    )
  }, [
    mobilePaySetting,
    cartHasPriceOverride,
    cartHasOpenItem,
    disablePPISplitOffline,
    isOrderTaxExempt,
    cfdMode,
    startSpinner,
    startCFDPayment,
    checkIfOfflineWithCallback,
    offlineModalScanScreenMessage,
    dispatch,
    handleScanScreenSelection,
    ppiConfigurationError,
  ])

  const ticketPayHandler = useCallback(() => {
    if (ppiConfigurationError) {
      dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
      return
    }
    checkIfOfflineWithCallback(
      () => {
        if (cfdMode === MODES.POS) {
          startSpinner()
          dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.TICKET, true))
          startCFDPayment(false)
          return
        }
        handleScanScreenSelection('/ticket')
      },
      offlineModalScanScreenMessage,
      disablePPISplitOffline,
    )
  }, [
    checkIfOfflineWithCallback,
    offlineModalScanScreenMessage,
    disablePPISplitOffline,
    cfdMode,
    handleScanScreenSelection,
    dispatch,
    startCFDPayment,
    startSpinner,
    ppiConfigurationError,
  ])

  const giftCardPayHandler = useCallback(() => {
    if (ppiConfigurationError) {
      dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.PPI_NETWORK_ERROR_MESSAGE))
      return
    }

    checkIfOfflineWithCallback(
      () => {
        if (cfdMode === MODES.POS) {
          startSpinner()
          dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.GIFT_CARD, true))
          startCFDPayment(false)
        } else {
          handleScanScreenSelection('/gift-card')
        }
      },
      offlineGiftCardModalMessage,
      disablePPISplitOffline,
    )
  }, [
    checkIfOfflineWithCallback,
    offlineGiftCardModalMessage,
    disablePPISplitOffline,
    cfdMode,
    dispatch,
    startSpinner,
    startCFDPayment,
    handleScanScreenSelection,
    ppiConfigurationError,
  ])

  const otherPaymentHandler = useCallback(() => dispatch(setRoute('/tender-selection')), [dispatch])

  const splitPaymentHandler = useCallback(
    () =>
      checkIfOfflineWithCallback(() => dispatch(setConfirmPaymentDialog(true)), offlineSubtext, !isUniqueInvoiceNumbersEnabled),
    [dispatch, checkIfOfflineWithCallback, offlineSubtext, isUniqueInvoiceNumbersEnabled],
  )

  // check for query string messages being sent from TenderSelect screen.
  useEffect(() => {
    if (cfdMode !== MODES.POS) return

    if (history?.location?.state?.payment === 'card') {
      startCFDPayment(false, true)
    } else if (history?.location?.state?.payment === 'rich_checkout') {
      startCFDPayment(true)
    }

    return () => {
      setIsCartLocked(false)
      clearTimeout(unlockTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Determines which payment buttons to show based on enabled tenders and other conditions
   * Always includes 'Card' and conditionally includes 'Cash', 'Mobile Pay', 'Ticket', 'Gift Card', 'Other' and 'Split'
   * Limits the number of buttons shown directly to 3, adding an 'Other' button if more are available
   * @returns {Set} A set of button names to be displayed
   */
  const getButtonsList = useCallback(() => {
    const trueButtonCount = !!cashEnabled + !!hasNoOpTender + !!mobilePaySetting + !!ticketEnabled + !!giftCardEnabled + 1
    const showOtherButton = trueButtonCount > 3

    const buttonsList = new Set(['Card'])
    if (cashEnabled) {
      buttonsList.add('Cash')
    }
    if (mobilePaySetting && buttonsList.size < 3) {
      buttonsList.add('Mobile Pay')
    }
    if (ticketEnabled && (buttonsList.size < 3 || (buttonsList.size < 4 && !showOtherButton))) {
      buttonsList.add('Ticket')
    }
    if (giftCardEnabled && (buttonsList.size < 3 || (buttonsList.size < 4 && !showOtherButton))) {
      buttonsList.add('Gift Card')
    }
    if (showOtherButton || (buttonsList.size < 4 && !!hasNoOpTender)) {
      buttonsList.add('Other')
    }
    if (cfdMode !== MODES.POS || isSDKCompatible(androidSDKVersion, '2.13.3')) {
      buttonsList.add('Split')
    }

    return buttonsList
  }, [cashEnabled, hasNoOpTender, mobilePaySetting, ticketEnabled, giftCardEnabled, cfdMode, androidSDKVersion])

  const buttonsSet = getButtonsList()

  // Rendering
  if (tabsModeEnabled) {
    return null
  }

  return (
    <div className={styles.payments}>
      {buttonsSet.has('Card') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={onCardPaymentHandler}>
          {'Card'}
        </Button>
      )}
      {buttonsSet.has('Cash') && (
        <Button useMui disabled={!hasCartItems} className={cn([styles.checkout, styles.stretch])} onClick={onCashPaymentHandler}>
          {'Cash'}
        </Button>
      )}
      {buttonsSet.has('Mobile Pay') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={mobilePayHandler}>
          {mobilePaySetting === 'pos_shows_qr' ? 'App' : ppiQRPayTender?.name ? ppiQRPayTender?.name : 'QR Pay'}
        </Button>
      )}
      {buttonsSet.has('Ticket') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={ticketPayHandler}>
          {ppiTicketTender?.name ? ppiTicketTender?.name : 'Ticket'}
        </Button>
      )}
      {buttonsSet.has('Gift Card') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={giftCardPayHandler}>
          {ppiGiftcardTender?.name ? ppiGiftcardTender?.name : 'Gift Card'}
        </Button>
      )}
      {buttonsSet.has('Other') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={otherPaymentHandler}>
          {'Other'}
        </Button>
      )}
      {buttonsSet.has('Split') && (
        <Button useMui className={cn([styles.checkout, styles.stretch])} disabled={!hasCartItems} onClick={splitPaymentHandler}>
          {'Split'}
        </Button>
      )}

      {offlineModal}
    </div>
  )
}

export default memo(VNPaymentButtons)
